import apiClient from "@/services/api/apiClient"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

/** How the pixels for a signature were produced. Purely informational —
 *  every source converges on the same trimmed PNG-with-alpha output before
 *  it reaches the API (uploads excepted, see the design doc). */
export type SignatureSource = "drawn" | "typed" | "uploaded"

/** Wire shape returned by the list/create/update endpoints. The image bytes
 *  never come along for the ride — `image_data` is a `deferred()` column on
 *  the backend, so these responses carry everything BUT the pixels. Fetch a
 *  thumbnail via `useOrgSignatureImage`. */
export interface OrganizationSignature {
  id: string
  signatoryName: string
  signatoryFunction: string | null
  source: SignatureSource
  contentType: string
  widthPx: number | null
  heightPx: number | null
  createdAt: Date
  updatedAt: Date
}

/** Body for POST /organization/signatures. `dataBase64` is always produced
 *  by rasterizing a canvas (drawn ink, typed text, or a manual crop) to PNG
 *  client-side — see `signatures/` — never sent as a raw File. The Upload
 *  tab still reuses `readAsBase64` from `organizationApi.ts` to read the
 *  chosen file into an `<img>` source for cropping. */
export interface CreateSignaturePayload {
  signatoryName: string
  signatoryFunction: string | null
  source: SignatureSource
  contentType: string
  dataBase64: string
  widthPx: number
  heightPx: number
}

const LIST_QUERY_KEY = ["organization", "signatures"] as const

function imageQueryKey(id: string | null) {
  return ["organization", "signatures", id, "image"] as const
}

/** The caller's OWN signatures only — the backend scopes list/create/rename/
 *  delete to the requesting user, not the whole org (a signature image is
 *  personal, not shared org branding), so this never returns a colleague's
 *  rows even though the endpoint is `/organization/signatures`.
 *
 *  `enabled` defaults to true for the settings-card caller, which only ever
 *  mounts once the flag is already on (SettingsPage gates it). The conformity
 *  editor caller must pass `flags.ENABLE_ORG_SIGNATURES` explicitly — it
 *  can't skip the hook call itself (rules of hooks: its own early return on
 *  the flag runs AFTER this), so without this the gated endpoint gets hit
 *  and 403s for every tenant the feature is off for. */
export function useOrgSignatures(enabled = true) {
  return useQuery({
    queryKey: LIST_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<OrganizationSignature[]>(
        "/organization/signatures",
      )
      return response.data
    },
    enabled,
  })
}

export function useCreateOrgSignature() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateSignaturePayload) => {
      const response = await apiClient.post<OrganizationSignature>(
        "/organization/signatures",
        body,
      )
      return response.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_QUERY_KEY })
    },
  })
}

export function useDeleteOrgSignature() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/organization/signatures/${id}`)
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: LIST_QUERY_KEY })
      // REMOVE, not invalidate: the image query is cached forever
      // (staleTime/gcTime Infinity, since a signature's pixels never change), so
      // an invalidate would make every still-mounted consumer REFETCH bytes that
      // no longer exist — a 204 DELETE followed by a 404 GET from the same page,
      // seen live on 2026-09-23. Dropping the entry leaves nothing to refetch.
      qc.removeQueries({ queryKey: imageQueryKey(id) })
    },
  })
}

/** Thumbnail bytes for one signature. This is the settings-list card's only
 *  caller of GET .../image — the conformity document preview never hits it,
 *  the data URI is inlined server-side into the preview HTML instead (see
 *  the design doc). Returns the raw Blob; turning it into an <img src> is a
 *  rendering-lifecycle concern (createObjectURL / revokeObjectURL) left to
 *  the component, not this API layer.
 *
 *  `staleTime`/`gcTime: Infinity` because a signature's pixels never change
 *  once created — only its name (PATCH) or existence (DELETE) can. */
export function useOrgSignatureImage(id: string | null) {
  return useQuery({
    queryKey: imageQueryKey(id),
    queryFn: async () => {
      const response = await apiClient.get<Blob>(
        `/organization/signatures/${id}/image`,
        { responseType: "blob" },
      )
      return response.data
    },
    enabled: id != null,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}
